import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent } from "react";

import {
  type LoginCredentials,
  type LoginVisualState,
  OperatorAuthController,
} from "../auth/OperatorAuthController";
import styles from "../styles/app.module.css";

interface LoginViewProps {
  readonly visualState: LoginVisualState;
  readonly statusMessage?: string;
  readonly onSubmit: (credentials: LoginCredentials) => void | Promise<void>;
  readonly onSubmitTotp?: (code: string) => void;
}

interface ConnectedLoginProps {
  readonly controller: OperatorAuthController;
}

export type LoginScreenProps = LoginViewProps | ConnectedLoginProps;

function ConnectedLoginScreen({ controller }: ConnectedLoginProps): JSX.Element {
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  return (
    <LoginView
      visualState={state.visualState}
      statusMessage={state.message}
      onSubmit={(credentials) => controller.submit(credentials)}
      onSubmitTotp={(code) => controller.submitTotp(code)}
    />
  );
}

export function LoginScreen(props: LoginScreenProps): JSX.Element {
  return "controller" in props ? <ConnectedLoginScreen {...props} /> : <LoginView {...props} />;
}

function LoginView({
  visualState,
  statusMessage = "",
  onSubmit,
  onSubmitTotp,
}: LoginViewProps): JSX.Element {
  const [validationMessage, setValidationMessage] = useState("");
  const feedbackRef = useRef<HTMLParagraphElement | null>(null);
  const locked = visualState === "loading" || visualState === "success";

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (locked) return;
    const formData = new FormData(event.currentTarget);
    const email = String(formData.get("email") ?? "").trim();
    const password = String(formData.get("password") ?? "");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || password.length < 6) {
      setValidationMessage("Informe um e-mail válido e uma senha com pelo menos 6 caracteres.");
      return;
    }
    setValidationMessage("");
    event.currentTarget.reset();
    void onSubmit({ email, password });
  };

  const submitTotp = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const code = String(formData.get("totp") ?? "").trim().replace(/\s/g, "");
    if (!/^[0-9]{6}$/.test(code)) {
      setValidationMessage("Digite um código numérico de 6 dígitos.");
      return;
    }
    setValidationMessage("");
    event.currentTarget.reset();
    onSubmitTotp?.(code);
  };

  const visibleMessage = validationMessage || statusMessage;
  const visibleState = validationMessage ? "error" : visualState;

  useEffect(() => {
    if (visibleState === "error" && visibleMessage.length > 0) {
      feedbackRef.current?.focus();
    }
  }, [visibleMessage, visibleState]);

  const shellContent = (
    <>
      <aside className={styles.loginContext} aria-label="Contexto do produto">
        <Brand fullName />
        <div className={styles.loginContextCopy}>
          <span className={styles.eyebrow}>Operação de suporte</span>
          <h1>Central interna</h1>
          <p>Chamados e sessões dos produtos integrados.</p>
        </div>
        <div className={styles.loginMeta}>Ambiente interno · acesso restrito</div>
      </aside>

      <main className={styles.loginPanel}>
        {visualState === "totp_pending" ? (
          <form
            className={styles.loginForm}
            data-component-id="cmp-totp-form"
            data-od-id="cmp-totp-form"
            data-state={visibleState}
            data-visual-state={visibleState}
            noValidate
            onSubmit={submitTotp}
          >
            <div className={styles.loginTitle}>
              <h2 id="login-title">Verificação em duas etapas</h2>
              <p>Abra o aplicativo autenticador e informe o código.</p>
            </div>

            {visibleMessage ? (
              <p
                id="login-feedback"
                className={visibleState === "error" ? styles.loginError : styles.loginStatus}
                ref={feedbackRef}
                role={visibleState === "error" ? "alert" : "status"}
                tabIndex={visibleState === "error" ? -1 : undefined}
              >
                {visibleMessage}
              </p>
            ) : null}

            <div className={styles.field}>
              <label htmlFor="totp">Código TOTP</label>
              <input
                className={styles.input}
                id="totp"
                name="totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                maxLength={6}
                pattern="[0-9]{6}"
                aria-describedby={visibleMessage ? "login-feedback" : undefined}
                aria-invalid={validationMessage ? true : undefined}
                autoFocus
                required
              />
            </div>

            <button
              className={`${styles.button} ${styles.primaryButton}`}
              id="action-totp"
              type="submit"
            >
              <span>Confirmar</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </form>
        ) : (
          <form
            className={styles.loginForm}
            data-component-id="cmp-login-form"
            data-od-id="cmp-login-form"
            data-state={visibleState}
            data-visual-state={visibleState}
            aria-busy={visualState === "loading"}
            noValidate
            onSubmit={submit}
          >
            <div className={styles.loginTitle}>
              <h2 id="login-title">Entrar</h2>
              <p>Use suas credenciais da software house.</p>
            </div>

            {visibleMessage ? (
              <p
                id="login-feedback"
                className={visibleState === "error" ? styles.loginError : styles.loginStatus}
                ref={feedbackRef}
                role={visibleState === "error" ? "alert" : "status"}
                tabIndex={visibleState === "error" ? -1 : undefined}
              >
                {visibleMessage}
              </p>
            ) : null}

            <div className={styles.field}>
              <label htmlFor="email">E-mail</label>
              <input
                className={styles.input}
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="nome@softwarehouse.com.br"
                aria-describedby={visibleMessage ? "login-feedback" : undefined}
                aria-invalid={validationMessage ? true : undefined}
                disabled={locked}
                required
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="password">Senha</label>
              <input
                className={styles.input}
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="Mínimo de 6 caracteres"
                aria-describedby={visibleMessage ? "login-feedback" : undefined}
                aria-invalid={validationMessage ? true : undefined}
                minLength={6}
                disabled={locked}
                required
              />
            </div>

            <button
              className={`${styles.button} ${styles.primaryButton}`}
              id="action-login"
              type="submit"
              disabled={locked}
            >
              <span>
                {visualState === "loading"
                  ? "Entrando…"
                  : visualState === "success"
                    ? "Acesso confirmado"
                    : "Entrar"}
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </form>
        )}
      </main>
    </>
  );

  return (
    <section
      id="login"
      className={styles.login}
      data-od-id="login"
    >
      <div className={styles.loginShell}>
        {shellContent}
      </div>
    </section>
  );
}

export function Brand({ fullName = false }: { readonly fullName?: boolean }): JSX.Element {
  return (
    <div className={styles.brand} aria-label="Suporte Decizyon">
      <img
        className={fullName ? styles.brandLogoFull : styles.brandLogo}
        src={fullName ? "/brand/logo-completo-sem-fundo-branco.png" : "/brand/logomarca-sem-fundo.png"}
        alt={fullName ? "Decizyon Tech" : ""}
      />
      <span>{fullName ? "Suporte Decizyon" : "Suporte"}</span>
    </div>
  );
}
